import React, {createContext, useContext, useMemo, useState} from 'react';

export type Notice = { id:string; ts:number; title:string; body?:string; level?:'info'|'warning'|'error' };
type Ctx = {
  notices: Notice[];
  unread: number;
  push: (n: Omit<Notice,'id'|'ts'>) => void;
  markAllRead: () => void;
  remove: (id:string) => void;
};

const NotifCtx = createContext<Ctx|null>(null);

export function NotificationsProvider({children}:{children:React.ReactNode}) {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [unread, setUnread] = useState(0);
  const push: Ctx['push'] = (n) => {
    const nn = { id: crypto.randomUUID(), ts: Date.now(), level: 'info' as const, ...n };
    setNotices(prev => [nn, ...prev]);
    setUnread(u => u+1);
  };
  const markAllRead = () => setUnread(0);
  const remove = (id:string) => setNotices(prev => prev.filter(n=>n.id!==id));
  const value = useMemo(()=>({notices, unread, push, markAllRead, remove}), [notices, unread]);
  return <NotifCtx.Provider value={value}>{children}</NotifCtx.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotifCtx);
  if (!ctx) throw new Error('useNotifications must be inside NotificationsProvider');
  return ctx;
}

export function NotificationsPanel() {
  const {notices, remove, markAllRead} = useNotifications();
  return (
    <div className="absolute right-2 top-14 z-20 w-[420px] max-w-[95vw] bg-white border border-slate-200 rounded-2xl shadow-lg">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="font-semibold">Notifications</div>
        <button className="btn-secondary" onClick={markAllRead}>Mark all read</button>
      </div>
      <div className="max-h-[60vh] overflow-auto">
        {notices.length===0 && <div className="p-4 text-sm text-slate-600">No notifications yet.</div>}
        {notices.map(n=>(
          <div key={n.id} className="px-4 py-3 border-b border-slate-100">
            <div className="text-sm font-medium">{n.title}</div>
            {n.body && <div className="text-xs text-slate-600">{n.body}</div>}
            <div className="text-[11px] text-slate-400 mt-1">{new Date(n.ts).toLocaleString()}</div>
            <div className="mt-2">
              <button className="btn-secondary" onClick={()=>remove(n.id)}>Dismiss</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
